import { supabase } from '../supabaseClient';

export const ATRIBUICAO_COPERNICUS = 'produced using Copernicus WorldDEM-30 © DLR e.V. 2010-2014 and © Airbus Defence and Space GmbH 2014-2018 provided under COPERNICUS by the European Union and ESA; all rights reserved';

export async function carregarElevacoesCopernicus(largura, altura, coordenada) {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (!data.session) throw new Error('Entre novamente no app para consultar o Copernicus.');

  const { fromUrl } = await import(
    /* webpackMode: "eager" */ 'geotiff'
  );
  const grupos = new Map();
  for (let y = 0; y < altura; y++) {
    for (let x = 0; x < largura; x++) {
      const { lng, lat } = coordenada(x, y);
      const oeste = Math.floor(lng);
      const sul = Math.floor(lat);
      const chave = `${oeste},${sul}`;
      if (!grupos.has(chave)) grupos.set(chave, { oeste, sul, indices: [] });
      grupos.get(chave).indices.push(y * largura + x);
    }
  }
  if (grupos.size > 16) throw new Error('Selecione uma área menor para consultar o Copernicus.');

  const elevacoes = new Float32Array(largura * altura).fill(NaN);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 90000);
  try {
    for (const { oeste, sul, indices } of grupos.values()) {
      const params = new URLSearchParams({ oeste: String(oeste), sul: String(sul) });
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/copernicus-dem?${params}`;
      const tiff = await fromUrl(url, {
        headers: {
          Authorization: `Bearer ${data.session.access_token}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        allowFullFile: false,
        blockSize: 64 * 1024,
        cacheSize: 32,
      }, controller.signal);
      try {
        const image = await tiff.getImage();
        const [origemLng, origemLat] = image.getOrigin();
        const [passoLng, passoLat] = image.getResolution();
        const deslocamento = image.pixelIsArea() ? 0.5 : 0;
        const pixelDaImagem = indice => {
          const { lng, lat } = coordenada(indice % largura, Math.floor(indice / largura));
          return {
            x: Math.max(0, Math.min(image.getWidth() - 1, (lng - origemLng) / passoLng - deslocamento)),
            y: Math.max(0, Math.min(image.getHeight() - 1, (lat - origemLat) / passoLat - deslocamento)),
          };
        };
        let xMin = image.getWidth() - 1;
        let yMin = image.getHeight() - 1;
        let xMax = 0;
        let yMax = 0;
        for (const indice of indices) {
          const { x, y } = pixelDaImagem(indice);
          xMin = Math.min(xMin, Math.floor(x));
          yMin = Math.min(yMin, Math.floor(y));
          xMax = Math.max(xMax, Math.ceil(x));
          yMax = Math.max(yMax, Math.ceil(y));
        }
        const [raster] = await image.readRasters({
          window: [xMin, yMin, xMax + 1, yMax + 1],
          samples: [0],
          signal: controller.signal,
        });
        const nodata = image.getGDALNoData();
        const rasterWidth = xMax - xMin + 1;
        for (const indice of indices) {
          const { x, y } = pixelDaImagem(indice);
          const x0 = Math.floor(x);
          const y0 = Math.floor(y);
          const x1 = Math.ceil(x);
          const y1 = Math.ceil(y);
          const valores = [
            raster[(y0 - yMin) * rasterWidth + x0 - xMin],
            raster[(y0 - yMin) * rasterWidth + x1 - xMin],
            raster[(y1 - yMin) * rasterWidth + x0 - xMin],
            raster[(y1 - yMin) * rasterWidth + x1 - xMin],
          ];
          if (!valores.every(valor => Number.isFinite(valor) && valor !== nodata)) continue;
          const dx = x - x0;
          const dy = y - y0;
          elevacoes[indice] = (valores[0] * (1 - dx) + valores[1] * dx) * (1 - dy)
            + (valores[2] * (1 - dx) + valores[3] * dx) * dy;
        }
      } finally {
        await tiff.close();
      }
    }
  } catch (error) {
    throw new Error(`Não foi possível carregar o Copernicus GLO-30. Verifique a conexão e a publicação da função copernicus-dem, ou selecione AWS Terrain Tiles para comparar. Detalhe: ${error.message}`);
  } finally {
    clearTimeout(timeout);
  }
  return elevacoes;
}
